<template>
  <fullscreen-overlay-frame :title="$t('gag-form-dialog.title')"
                            icon="feed"
                            color="primary"
                            centered
                            closable
                            @close="closeOverlay">

    <template v-slot:content>
      <div v-text="$t('gag-form-dialog.text')"
           class="mb-8"/>

      <v-form v-model="valid">
        <v-text-field outlined
                      v-model="street"
                      :label="$t('location-dialog.street')"
                      :rules="requiredRules"/>

        <v-row no-gutters>
          <v-col cols="4" class="mr-4">
            <v-text-field outlined
                          v-model="zip"
                          :label="$t('location-dialog.zip')"
                          :rules="requiredRules"/>
          </v-col>
          <v-col>
            <v-text-field outlined
                          v-model="city"
                          :label="$t('location-dialog.city')"
                          :rules="requiredRules"/>
          </v-col>
        </v-row>

        <v-text-field outlined
                      v-model="phone"
                      :label="$t('gag-form-dialog.phone')"
                      :rules="requiredRules"/>

        <v-textarea outlined
                    v-model="userMessage"
                    counter="2000"
                    height="300"
                    no-resize
                    :label="$t('form-dialog.textarea.label')"
                    :rules="messageRules"/>

        <v-file-input outlined chips
                      if="fileInput"
                      v-model="file"
                      :label="$t('form-dialog.file-input.label')"
                      class="mt-4"
                      show-size
                      accept="image/png, image/jpeg, image/jpg, application/pdf"
                      :rules="fileRules"
                      prepend-icon="">
          <template v-slot:append>
            <label for="#fileInput"> <!-- opens file selection when clicked -->
              <v-icon color="primary">attach_file</v-icon>
            </label>
            <v-btn v-if="showCaptureButton"
                   icon small
                   class="ml-2"
                   @click="openCamera">
              <v-icon color="primary">photo_camera</v-icon>
            </v-btn>
          </template>
        </v-file-input>
      </v-form>
    </template>

    <template v-slot:actions>
      <v-btn large depressed
             color="primary"
             class="font-weight-bold action-button"
             :disabled="!valid"
             :loading="sending"
             @click="sendForm">
        {{ $t('app.send') }}
      </v-btn>
    </template>
  </fullscreen-overlay-frame>
</template>

<script>
import FullscreenOverlayFrame from "@/templates/dialogs/FullscreenOverlayFrame";

export default {
  /* global Camera */ // resolve Cordova Camera Plugin
  name: 'GagFormDialog',

  components: {
    FullscreenOverlayFrame
  },

  props: ['data'],

  data: () => ({
    maxFileSize: 3000000, // 3 MB
    file: null,
    sending: false,
    street: '',
    zip: '',
    city: '',
    phone: '',
    userMessage: '',
    valid: true
  }),

  methods: {
    /**
     * This method opens the camera to take picture
     */
    openCamera () {
      let self = this
      navigator.camera.getPicture(function (url) {
            self.getFileContentAsBase64(url)
          }, function (err) {
            console.error(err)
          },
          {
            // returns path to image
            destinationType: Camera.DestinationType.FILE_URI,
            // opens camera
            sourceType: Camera.PictureSourceType.CAMERA,
            // returns file of type jpeg
            encodingType: Camera.EncodingType.JPEG,
            // quality of image (100 would be full resolution)
            quality: 50
          });
    },

    /**
     * This method gets base64 content of a photo file. Takes url to that file as parameter
     *
     * @param url -> string
     */
    getFileContentAsBase64: function (url) {
      let self = this
      window.resolveLocalFileSystemURL(url, function success(fileEntry) {
        fileEntry.file(
            function success(file) {

              if (file.size > self.maxFileSize) {
                this.$root.bisatoast.error({message: self.$t('form-dialog.rules.file-too-large')})
              }

              let reader = new FileReader();
              reader.onload = function () {
              }
              reader.onloadend = function () {
                // create Blob from file because cordova-camera-plugin image-files wouldn't be sent
                self.createBlob(this.result, file.name)
              }
              reader.onerror = function (error) {
                console.error(error);
              }

              reader.readAsDataURL(file);

            }, function (error) {
              console.error(error)
            })
      }, function (error) {
        console.error(error)
      })
    },

    /**
     * This method takes base64 file and creates and returns blob
     *
     * @param base64File -> string
     * @param fileName
     */
    createBlob (base64File, fileName) {
      let block = base64File.split(";");
      let contentType = block[0].split(":")[1];
      let realData = block[1].split(",")[1];
      let blob = this.b64toBlob(realData, contentType);
      blob.name = fileName
      this.file = blob
    },

    /**
     * This method creates blob
     *
     * @param b64Data -> string
     * @param contentType -> string
     * @param sliceSize
     */
    b64toBlob (b64Data, contentType = '', sliceSize = 512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, {type: contentType});
    },

    /**
     * This method submits the form which was filled by the user to the API
     */
    sendForm () {
      this.sending = true
      let formData = new FormData();
      if (this.file) {
        formData.append('attachment', this.file, this.file.name)
      }

      let body = this.$t('gag-form-dialog.mail-body', {street: this.street, zip: this.zip, city: this.city, phone: this.phone, message: this.userMessage})
      formData.append('body', body)
      formData.append('noSignature', true)

      this.$rhRequest.sendPost({
        endpoint: 'mail/send-gag-form-mail',
        serializer: 'multipart',
        header: {
          'Content-Type': 'multipart/form-data'
        },
        data: formData
      }, () => {
        this.sending = false
        this.closeOverlay()
        this.$root.bisatoast.success({message: this.$t('form-dialog.send.success'), showCloseBtn: true})

      }, (error) => {
        this.sending = false
        console.error(error)
        this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
      })
    },

    /**
     * on dialog close
     */
    closeOverlay() {
      this.file = null
      this.userMessage = ''
      this.$root.bisadialog.toggle('gagForm')
    }
  },

  computed: {
    fileRules() {
      return [
        value => !value || value.size < this.maxFileSize || this.$t('form-dialog.rules.file-too-large'),
      ]
    },

    messageRules() {
      return [
        v => !!v || this.$t('app.rules.required'),
        v => (v && v.length <= 2000) || this.$t('app.rules.too-long', {maxLength: 2000})
      ]
    },

    requiredRules() {
      return [
        v => !!v || this.$t('app.rules.required')
      ]
    },

    showCaptureButton() {
      // show capture image button only on android devices
      // reason is, if file selection gets opened on ios device, the option "take picture" can't be disabled,
      // therefore the button "take picture" is redundant for ios devices
      return window?.cordova?.platformId.toLowerCase() === "android";
    }
  },
};
</script>

